import { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import {
  getAuditOptions, previewAudit, startAudit, getOngoingAudits, getAuditReports,
  getAuditReport, reassignAudit, cancelAudit, closeAudit,
} from '../api/audit';

// Brand Ops → Audit. Initiate an audit for a packer, watch it in Ongoing, then
// review it in Past audit reports: reassign the not-found pieces to another
// packer, or close it. Only closing writes anything to inventory.

const EXCLUDE_OPTIONS = [
  { value: '', label: "Don't leave any out" },
  { value: '3', label: 'Found by a picker in the last 3 days' },
  { value: '7', label: 'Found by a picker in the last week' },
  { value: '30', label: 'Found by a picker in the last month' },
];

const STATUS_LABELS = {
  in_bin: 'In bin', inbounded: 'Received, not put away', in_mini_pack: 'Mini-pack',
  in_full_pack: 'In a pack', in_transit: 'In transit', in_ironing_queue: 'Ironing queue',
  in_repair_queue: 'Repair queue', in_retag_queue: 'Retag queue',
  in_search_queue: 'Search queue', reversed: 'Restock queue',
  sold: 'Sold', missing: 'Missing', damaged: 'Damaged', returned_to_brand: 'Returned to brand',
};

const OUTCOME_LABELS = {
  found: 'Found — logged in item history',
  sent_to_search_queue: 'Sent to search queue',
  marked_missing: 'Marked missing',
  already_in_search_queue: 'Already in search queue',
  not_moved_in_order: 'Not moved — in an order',
  not_moved_status_changed: 'Not moved — moved during audit',
  not_moved_item_gone: 'Not moved — piece not found in ZOCS',
};

const fmtDate = iso => (iso ? new Date(iso).toLocaleString('en-IN', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const csvCell = v => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function filtersText(filters, brandNames) {
  if (!filters) return '—';
  const parts = [];
  if (filters.brand_ids?.length) parts.push(`Brands: ${filters.brand_ids.map(id => brandNames[id] || id).join(', ')}`);
  if (filters.categories?.length) parts.push(`Categories: ${filters.categories.join(', ')}`);
  if (filters.use_cases?.length) parts.push(`Use-cases: ${filters.use_cases.join(', ')}`);
  if (filters.sizes?.length) parts.push(`Sizes: ${filters.sizes.join(', ')}`);
  if (filters.barcodes?.length) parts.push(`${filters.barcodes.length} barcode(s)`);
  if (filters.exclude_found_days) parts.push(`Left out pieces found in last ${filters.exclude_found_days} days`);
  return parts.length ? parts.join(' · ') : 'Everything on our books';
}

function downloadReportCsv(report, brandNames) {
  const lines = [
    ['Audit', report.reference],
    ['Filters', filtersText(report.filters, brandNames)],
    ['Packers', report.rounds.map(r => `Round ${r.round_no}: ${r.assigned_packer_name || '—'}`).join('; ')],
    ['Pieces', report.item_count],
    ['Found', report.found_count],
    ['Not found', report.not_found_count],
    ['% found', `${report.found_pct}%`],
    ['Closed', report.closed_at ? `${fmtDate(report.closed_at)} by ${report.closed_by_name || '—'}` : 'Not closed'],
    ['Not-found action', report.close_action === 'missing' ? 'Marked missing' : report.close_action === 'search_queue' ? 'Sent to search queue' : '—'],
    [],
    ['Zoddle barcode', 'Brand', 'Design', 'Design code', 'Category', 'Size', 'Gender',
     'Status at start', 'Location at start', 'Status at submission', 'Location at submission',
     'Result', 'Found in round', 'Outcome', 'Status now'],
    ...report.items.map(it => [
      it.zoddle_barcode, it.brand_name, it.design_name, it.design_code, it.category, it.size, it.gender,
      STATUS_LABELS[it.status_at_start] || it.status_at_start, it.location_at_start,
      STATUS_LABELS[it.status_at_submit] || it.status_at_submit || '',
      STATUS_LABELS[it.location_at_submit] || it.location_at_submit || '',
      it.result === 'found' ? 'Found' : it.result === 'not_found' ? 'Not found' : '',
      it.found_round || '', OUTCOME_LABELS[it.final_outcome] || '',
      STATUS_LABELS[it.current_status] || it.current_status,
    ]),
  ];
  const csv = lines.map(l => l.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${report.reference}_audit_report.csv`; a.click();
  URL.revokeObjectURL(url);
}

// ── Initiate ───────────────────────────────────────────────────────────────

function Initiate({ options, onStarted }) {
  const [brandIds, setBrandIds] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [barcodeText, setBarcodeText] = useState('');
  const [sizes, setSizes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [useCases, setUseCases] = useState([]);
  const [exclude, setExclude] = useState('');
  const [packerId, setPackerId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const filters = useMemo(() => ({
    brand_ids: brandIds,
    barcodes: barcodeText.split(/[\s,]+/).map(s => s.trim()).filter(Boolean),
    sizes,
    categories,
    use_cases: useCases,
    exclude_found_days: exclude ? parseInt(exclude, 10) : null,
  }), [brandIds, barcodeText, sizes, categories, useCases, exclude]);

  // Live count as the filters change.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try { const p = await previewAudit(filters); if (!cancelled) setPreview(p); }
      catch (e) { if (!cancelled) setPreview(null); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filters]);

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  async function start() {
    if (!packerId || !preview?.count) return;
    const packer = options.packers.find(p => p.staff_id === packerId);
    if (!window.confirm(`Start an audit of ${preview.count} piece(s) for ${packer?.full_name}?`)) return;
    setBusy(true); setError(null);
    try {
      const r = await startAudit(filters, packerId);
      setBrandIds([]); setBarcodeText(''); setSizes([]); setCategories([]); setUseCases([]); setExclude(''); setPackerId('');
      onStarted(`${r.reference} started — ${r.item_count} piece(s) assigned to ${r.assigned_packer_name}.`);
    } catch (e) { setError(e.response?.data?.detail || 'Could not start the audit.'); }
    finally { setBusy(false); }
  }

  const shownBrands = options.brands.filter(b =>
    b.name.toLowerCase().includes(brandSearch.toLowerCase()));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-700">Brand {brandIds.length > 0 && <span className="text-gray-400 font-normal">· {brandIds.length} picked</span>}</h3>
            {brandIds.length > 0 && <button onClick={() => setBrandIds([])} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>}
          </div>
          <input value={brandSearch} onChange={e => setBrandSearch(e.target.value)} placeholder="Search brands"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mb-2" />
          <div className="max-h-48 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
            {shownBrands.map(b => (
              <label key={b.brand_partner_id} className="flex items-center gap-2 text-sm text-gray-700 truncate">
                <input type="checkbox" checked={brandIds.includes(b.brand_partner_id)}
                  onChange={() => toggle(brandIds, setBrandIds, b.brand_partner_id)} />
                <span className="truncate">{b.name}</span>
              </label>
            ))}
          </div>
        </div>

        {[
          { title: 'Category', list: options.categories || [], picked: categories, set: setCategories, note: null },
          { title: 'Use-case', list: options.use_cases || [], picked: useCases, set: setUseCases,
            note: 'A piece counts if any of its use-case tags matches.' },
        ].map(box => (
          <div key={box.title} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-gray-700">{box.title}</h3>
              <span className="text-xs text-gray-400">{box.picked.length ? `${box.picked.length} picked` : 'none picked = all'}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {box.list.map(v => (
                <button key={v} onClick={() => toggle(box.picked, box.set, v)}
                  className={`text-xs font-semibold px-3 py-1 rounded-full border transition ${box.picked.includes(v) ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>
                  {v}
                </button>
              ))}
            </div>
            {box.note && <p className="text-xs text-gray-400 mt-2">{box.note}</p>}
          </div>
        ))}

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-2">Size</h3>
          <div className="flex flex-wrap gap-1.5">
            {options.sizes.map(s => (
              <button key={s} onClick={() => toggle(sizes, setSizes, s)}
                className={`text-xs font-semibold px-3 py-1 rounded-full border transition ${sizes.includes(s) ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-2">Zoddle barcodes <span className="text-gray-400 font-normal">· optional, paste a list</span></h3>
          <textarea value={barcodeText} onChange={e => setBarcodeText(e.target.value)} rows={3}
            placeholder="Separated by commas, spaces or new lines"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
          {preview?.barcodes_not_in_scope?.length > 0 && (
            <p className="text-xs text-zoddle-pink mt-1">
              Not included (not found, or sold / damaged / missing / returned): {preview.barcodes_not_in_scope.join(', ')}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Leave out</label>
            <select value={exclude} onChange={e => setExclude(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {EXCLUDE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Packer</label>
            <select value={packerId} onChange={e => setPackerId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Select a packer</option>
              {options.packers.map(p => <option key={p.staff_id} value={p.staff_id}>{p.full_name}</option>)}
            </select>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-3xl font-black text-gray-800">{preview ? preview.count : '…'}</p>
          <p className="text-sm text-gray-500 mb-2">piece(s) in this audit</p>
          {preview && Object.keys(preview.by_status).length > 0 && (
            <ul className="text-xs text-gray-500 space-y-0.5 mb-2">
              {Object.entries(preview.by_status).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                <li key={s}>{STATUS_LABELS[s] || s}: {n}</li>
              ))}
            </ul>
          )}
          {preview?.excluded_recently_found > 0 && <p className="text-xs text-gray-400">{preview.excluded_recently_found} left out — found recently</p>}
          {preview?.excluded_in_open_audit > 0 && <p className="text-xs text-gray-400">{preview.excluded_in_open_audit} left out — already in an open audit</p>}
          {error && <p className="text-xs text-zoddle-pink mt-2">{error}</p>}
          <button onClick={start} disabled={busy || !packerId || !preview?.count}
            className="w-full mt-3 bg-zoddle-green text-white text-sm font-bold py-2.5 rounded-lg disabled:opacity-50">
            {busy ? 'Starting…' : 'Start audit'}
          </button>
          {!packerId && <p className="text-xs text-gray-400 mt-1 text-center">Pick a packer to start.</p>}
        </div>
      </div>
    </div>
  );
}

// ── Ongoing ────────────────────────────────────────────────────────────────

function Ongoing({ onChanged }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try { setRows(await getOngoingAudits()); } catch (e) { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function cancel(a) {
    if (!window.confirm(`Cancel ${a.reference}? Nothing has been written to inventory, so nothing needs undoing.`)) return;
    try { await cancelAudit(a.audit_id); onChanged(`${a.reference} cancelled.`); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Could not cancel.'); }
  }

  if (rows === null) return <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>;
  if (rows.length === 0) return <div className="text-center py-16 text-gray-400 text-sm">No audits in progress.</div>;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
          <tr>
            <th className="text-left px-4 py-2">Audit</th><th className="text-left px-4 py-2">Packer</th>
            <th className="text-left px-4 py-2">Progress</th><th className="text-left px-4 py-2">Started</th><th />
          </tr>
        </thead>
        <tbody>
          {rows.map(a => (
            <tr key={a.audit_id} className="border-t border-gray-100">
              <td className="px-4 py-2 font-semibold text-gray-800">{a.reference}{a.round_no > 1 && <span className="text-xs text-gray-400 font-normal"> · round {a.round_no}</span>}</td>
              <td className="px-4 py-2">{a.assigned_packer_name || '—'}</td>
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-28 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-zoddle-green" style={{ width: `${a.item_count ? (100 * a.answered_count / a.item_count) : 0}%` }} />
                  </div>
                  <span className="text-xs text-gray-500">{a.answered_count}/{a.item_count}</span>
                </div>
              </td>
              <td className="px-4 py-2 text-gray-500">{fmtDate(a.created_at)}</td>
              <td className="px-4 py-2 text-right">
                <button onClick={() => cancel(a)} className="text-xs text-gray-400 hover:text-zoddle-pink">Cancel</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Past reports ───────────────────────────────────────────────────────────

function ReportDetail({ rootId, options, brandNames, onBack, onChanged, onMsg }) {
  const [report, setReport] = useState(null);
  const [reassignTo, setReassignTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState('not_found');

  const load = useCallback(async () => {
    try { setReport(await getAuditReport(rootId)); } catch (e) { setReport(false); }
  }, [rootId]);
  useEffect(() => { load(); }, [load]);

  if (report === null) return <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>;
  if (report === false) return <div className="text-center py-16 text-gray-400 text-sm">Could not load this report.</div>;

  const awaiting = report.status === 'submitted';
  const latest = report.rounds[report.rounds.length - 1];

  async function reassign() {
    const packer = options.packers.find(p => p.staff_id === reassignTo);
    if (!window.confirm(`Send the ${report.not_found_count} not-found piece(s) to ${packer?.full_name} as a new round?`)) return;
    setBusy(true);
    try {
      const r = await reassignAudit(report.latest_audit_id, reassignTo);
      onChanged(`${r.reference} started — ${r.item_count} piece(s) assigned to ${r.assigned_packer_name}.`);
      onBack();
    } catch (e) { alert(e.response?.data?.detail || 'Could not reassign.'); }
    finally { setBusy(false); }
  }

  async function close(action) {
    const what = action === 'missing' ? 'marked MISSING' : 'sent to the SEARCH QUEUE';
    if (!window.confirm(
      `Close ${report.reference}?\n\n` +
      `• ${report.found_count} found piece(s) get a "found in audit" entry in their item history.\n` +
      `• ${report.not_found_count} not-found piece(s) will be ${what} — except any that are in an order or moved during the audit, which are left alone.\n\n` +
      'This writes to inventory and cannot be undone from here.')) return;
    setBusy(true);
    try {
      const r = await closeAudit(report.latest_audit_id, action);
      setReport(r.report);
      downloadReportCsv(r.report, brandNames);
      onMsg(`${report.reference} closed — report downloaded.`);
    } catch (e) { alert(e.response?.data?.detail || 'Could not close.'); }
    finally { setBusy(false); }
  }

  const shown = report.items.filter(it => show === 'all' || it.result === show);
  const outcomeCounts = {};
  report.items.forEach(it => { if (it.final_outcome) outcomeCounts[it.final_outcome] = (outcomeCounts[it.final_outcome] || 0) + 1; });

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-400 hover:text-gray-600 mb-3">← All reports</button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800">{report.reference}</h2>
          <p className="text-xs text-gray-500">{filtersText(report.filters, brandNames)}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {report.rounds.map(r => `Round ${r.round_no}: ${r.assigned_packer_name || '—'} (${r.found_count}/${r.item_count} found)`).join(' · ')}
          </p>
        </div>
        <button onClick={() => downloadReportCsv(report, brandNames)}
          className="border border-gray-300 text-gray-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gray-50">
          Download report
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[['Pieces', report.item_count], ['Found', report.found_count],
          ['Not found', report.not_found_count], ['% found', `${report.found_pct}%`]].map(([k, v]) => (
          <div key={k} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-400">{k}</p>
            <p className="text-2xl font-black text-gray-800">{v}</p>
          </div>
        ))}
      </div>

      {awaiting && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-1">Reassign not-found pieces</h3>
            <p className="text-xs text-gray-400 mb-2">Another packer looks for the {report.not_found_count} piece(s) round {latest.round_no} couldn't find. Nothing is written to inventory.</p>
            <div className="flex gap-2">
              <select value={reassignTo} onChange={e => setReassignTo(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Select a packer</option>
                {options.packers.map(p => <option key={p.staff_id} value={p.staff_id}>{p.full_name}</option>)}
              </select>
              <button onClick={reassign} disabled={busy || !reassignTo || !report.not_found_count}
                className="bg-zoddle-blue text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">Reassign</button>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-1">Close audit</h3>
            <p className="text-xs text-gray-400 mb-2">Writes the results to inventory and downloads the report. Choose what happens to the not-found pieces:</p>
            <div className="flex gap-2">
              <button onClick={() => close('search_queue')} disabled={busy}
                className="flex-1 bg-zoddle-green text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50">Send to search queue</button>
              <button onClick={() => close('missing')} disabled={busy}
                className="flex-1 bg-zoddle-pink text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50">Mark missing</button>
            </div>
          </div>
        </div>
      )}

      {report.status === 'closed' && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-4 text-sm text-gray-600">
          Closed {fmtDate(report.closed_at)} by {report.closed_by_name || '—'} ·{' '}
          {Object.entries(outcomeCounts).map(([k, n]) => `${OUTCOME_LABELS[k] || k}: ${n}`).join(' · ')}
        </div>
      )}

      <div className="flex gap-1.5 mb-3">
        {[['not_found', `Not found (${report.not_found_count})`], ['found', `Found (${report.found_count})`], ['all', 'All']].map(([k, label]) => (
          <button key={k} onClick={() => setShow(k)}
            className={`text-xs font-semibold px-3 py-1 rounded-full border ${show === k ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 text-gray-500'}`}>{label}</button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="px-3 py-2" /><th className="text-left px-3 py-2">Barcode</th>
              <th className="text-left px-3 py-2">Brand · size</th><th className="text-left px-3 py-2">Where at start</th>
              <th className="text-left px-3 py-2">At submission</th>
              <th className="text-left px-3 py-2">Result</th><th className="text-left px-3 py-2">{report.status === 'closed' ? 'Outcome' : 'Status now'}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(it => (
              <tr key={it.item_id} className="border-t border-gray-100">
                <td className="px-3 py-1.5 w-12">
                  {it.image_url_1
                    ? <img src={cldThumb(it.image_url_1, 120)} alt="" className="w-10 h-10 object-cover rounded" loading="lazy" />
                    : <div className="w-10 h-10 bg-gray-100 rounded" />}
                </td>
                <td className="px-3 py-1.5 font-mono">{it.zoddle_barcode}</td>
                <td className="px-3 py-1.5">{it.brand_name || '—'} · {it.size}</td>
                <td className="px-3 py-1.5 text-gray-600">{it.location_at_start}</td>
                <td className={`px-3 py-1.5 ${it.location_at_submit && it.location_at_submit !== it.location_at_start ? 'text-gray-800 font-semibold' : 'text-gray-600'}`}>
                  {it.location_at_submit ? (STATUS_LABELS[it.location_at_submit] || it.location_at_submit) : '—'}
                </td>
                <td className="px-3 py-1.5">
                  {it.result === 'found'
                    ? <span className="text-xs font-semibold text-zoddle-green">Found{report.rounds.length > 1 ? ` (round ${it.found_round})` : ''}</span>
                    : <span className="text-xs font-semibold text-zoddle-pink">Not found</span>}
                </td>
                <td className="px-3 py-1.5 text-xs text-gray-500">
                  {report.status === 'closed'
                    ? (OUTCOME_LABELS[it.final_outcome] || '—')
                    : (STATUS_LABELS[it.current_status] || it.current_status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">Nothing here.</div>}
      </div>
    </div>
  );
}

function Reports({ options, brandNames, onChanged, onMsg }) {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    try { setRows(await getAuditReports()); } catch (e) { setRows([]); }
  }, []);
  useEffect(() => { if (!openId) load(); }, [load, openId]);

  if (openId) {
    return <ReportDetail rootId={openId} options={options} brandNames={brandNames}
      onBack={() => setOpenId(null)} onChanged={onChanged} onMsg={onMsg} />;
  }
  if (rows === null) return <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>;
  if (rows.length === 0) return <div className="text-center py-16 text-gray-400 text-sm">No submitted audits yet.</div>;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
          <tr>
            <th className="text-left px-4 py-2">Audit</th><th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Packer(s)</th><th className="text-left px-4 py-2">Found</th>
            <th className="text-left px-4 py-2">Started</th><th className="text-left px-4 py-2">Closed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.root_audit_id} onClick={() => setOpenId(r.root_audit_id)}
              className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
              <td className="px-4 py-2 font-semibold text-gray-800">{r.reference}</td>
              <td className="px-4 py-2">
                {r.status === 'closed'
                  ? <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Closed</span>
                  : <span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">Awaiting review</span>}
              </td>
              <td className="px-4 py-2 text-gray-600">{r.packers.filter(Boolean).join(' → ')}</td>
              <td className="px-4 py-2">{r.found_count}/{r.item_count} <span className="text-gray-400">({r.item_count ? Math.round(1000 * r.found_count / r.item_count) / 10 : 0}%)</span></td>
              <td className="px-4 py-2 text-gray-500">{fmtDate(r.created_at)}</td>
              <td className="px-4 py-2 text-gray-500">{r.closed_at ? fmtDate(r.closed_at) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

const SECTIONS = [
  { key: 'initiate', label: 'Initiate' },
  { key: 'ongoing', label: 'Ongoing audits' },
  { key: 'reports', label: 'Past audit reports' },
];

export default function Audit() {
  const [section, setSection] = useState('initiate');
  const [options, setOptions] = useState(null);
  const [msg, setMsg] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    getAuditOptions().then(setOptions).catch(() => setOptions({ brands: [], sizes: [], packers: [] }));
  }, []);
  const brandNames = useMemo(() => Object.fromEntries(
    (options?.brands || []).map(b => [b.brand_partner_id, b.name])), [options]);

  const changed = text => { setMsg(text); setRefreshKey(k => k + 1); };

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-5">
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => { setSection(s.key); setMsg(null); }}
            className={`text-sm font-semibold px-4 py-1.5 rounded-full border transition ${section === s.key ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500 hover:text-gray-700'}`}>
            {s.label}
          </button>
        ))}
      </div>
      {msg && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-2 mb-4">{msg}</div>}
      {!options ? <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
      : section === 'initiate' ? <Initiate options={options} onStarted={changed} />
      : section === 'ongoing' ? <Ongoing key={refreshKey} onChanged={changed} />
      : <Reports key={refreshKey} options={options} brandNames={brandNames} onChanged={changed} onMsg={setMsg} />}
    </div>
  );
}
